/**
 * auth/AuthContext.jsx — React context that exposes the current user and token.
 *
 * After Azure AD login, the app calls POST /api/auth/callback to exchange the
 * auth code and create/retrieve the user record. The returned user object and
 * access token are stored in context and sessionStorage.
 */

import { createContext, useContext, useEffect, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { loginRequest } from './msalConfig'
import apiClient from '../services/apiClient'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal()
  const [user, setUser] = useState(() => {
    const stored = sessionStorage.getItem('aipulse_user')
    return stored ? JSON.parse(stored) : null
  })
  const [token, setToken] = useState(() => sessionStorage.getItem('aipulse_token') || null)
  const [loading, setLoading] = useState(true)
  const [authError, setAuthError] = useState(
    () => sessionStorage.getItem('aipulse_auth_error') === 'true'
  )
  const [effectiveRole, setEffectiveRole] = useState(() => {
    const stored = sessionStorage.getItem('aipulse_effective_role')
    if (stored) return stored
    const storedUser = sessionStorage.getItem('aipulse_user')
    return storedUser ? JSON.parse(storedUser).app_role : null
  })

  // Sync effectiveRole once user is available after first login (state init ran before user was set)
  useEffect(() => {
    if (user && effectiveRole === null) {
      setEffectiveRole(user.app_role)
    }
  }, [user, effectiveRole])

  // When STAGE=local, skip the entire Azure AD flow — token is already set
  // via the "Login as Local Admin" button on the Login page.
  useEffect(() => {
    if (import.meta.env.VITE_LOCAL_DEV === 'true') {
      setLoading(false)
      return
    }

    // When MSAL finishes an interaction and we have an account but no user yet,
    // acquire a token silently and call /auth/callback to upsert the user.
    if (inProgress !== InteractionStatus.None) return
    if (accounts.length === 0) {
      setLoading(false)
      return
    }
    if (authError) {
      setLoading(false)
      return
    }
    if (user && token) {
      setLoading(false)
      return
    }

    instance
      .acquireTokenSilent({ ...loginRequest, account: accounts[0] })
      .then(async (result) => {
        const accessToken = result.accessToken
        setToken(accessToken)
        sessionStorage.setItem('aipulse_token', accessToken)

        const response = await apiClient.post('/auth/callback', {
          code: accessToken,           // we send the access token directly
          redirect_uri: window.location.origin + '/auth/callback',
        })
        const userData = response.data.user
        setUser(userData)
        sessionStorage.setItem('aipulse_user', JSON.stringify(userData))
      })
      .catch((err) => {
        console.error('AuthContext: callback failed', err)
        // Set authError to stop the retry loop; token cleared so user sees login page
        setAuthError(true)
        sessionStorage.setItem('aipulse_auth_error', 'true')
        sessionStorage.removeItem('aipulse_token')
        setToken(null)
      })
      .finally(() => setLoading(false))
  }, [accounts, inProgress, instance, user, token, authError])

  const login = () => instance.loginRedirect(loginRequest)

  const switchRole = (role) => {
    setEffectiveRole(role)
    sessionStorage.setItem('aipulse_effective_role', role)
  }

  const logout = () => {
    sessionStorage.removeItem('aipulse_user')
    sessionStorage.removeItem('aipulse_token')
    sessionStorage.removeItem('aipulse_effective_role')
    sessionStorage.removeItem('aipulse_auth_error')
    setUser(null)
    setToken(null)
    setEffectiveRole(null)
    instance.logoutRedirect()
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, authError, login, logout, effectiveRole, switchRole }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
